/**
 * Sub-tab "Visão Geral" do Controle PLD (Task #190 Fase 2).
 *
 * 4 KPIs (Realizado / Semana atual / Min / Max) + gráfico do PLD diário do mês
 * (submercado selecionado) + tabela "Resumo por Semana Operativa ONS".
 */
import { useMemo, useState } from "react";
import type { PldSubmercado } from "../../../api/types";
import { usePldOverview } from "../../../hooks/usePldOverview";
import {
  invalidatePldProjecaoCache,
  usePldProjecao,
} from "../../../hooks/usePldProjecao";
import {
  invalidatePldSemanasCache,
  usePldSemanas,
} from "../../../hooks/usePldSemanas";
import { usePldProjecaoLog } from "../../../hooks/usePldProjecaoLog";
import { AtualizarDecompForm } from "./AtualizarDecompForm";
import { KpiCard } from "../../ui/KpiCard";
import { PldLineChart, type PldBand, type PldSeries } from "./PldLineChart";
import { PldEmpty, PldError, PldLoading } from "./PldStates";
import {
  SUBMERCADO_COLOR,
  SUBMERCADO_LABEL,
  fmtDateBR,
  fmtPld,
  mesLabel,
  mesRefCorrente,
  statusBadge,
} from "./pldShared";

// Cor da projeção (âmbar = "futuro/estimado"), distinta do realizado.
const COR_PROJECAO = "#F5A623";
// #26: cor da projeção AS-OF (violeta) — track record sobre o histórico,
// distinta do realizado (cor do submercado) e da projeção futura (âmbar).
const COR_ASOF = "#B084EB";

// #217: DIF Dessem×Decomp — abaixo do DECOMP (negativo) vs acima (positivo).
function classBySign(v: number): string {
  if (v < 0) return "text-red-400";
  if (v > 0) return "text-indra-turquesa";
  return "text-white/50";
}

// DIF com sinal explícito (+/−) e 2 casas; "—" quando não calculável.
function fmtDif(v: number | null | undefined): string {
  if (v == null) return "—";
  const s = fmtPld(Math.abs(v)); // "R$ 21,09"
  if (v > 0) return `+${s}`;
  if (v < 0) return `−${s}`;
  return s;
}

interface Props {
  mesRef: string;
  submercado: PldSubmercado;
}

export function PldVisaoGeralTab({ mesRef, submercado }: Props) {
  const overview = usePldOverview(mesRef, submercado);
  // Task #210/#222: 1 nonce dispara o refetch da projeção E da tabela de semanas
  // após inserir nova REV DECOMP (a tabela depende de decomp_revisao+projeção
  // desde #216/#217). Declarado antes dos hooks que o consomem.
  const [decompNonce, setDecompNonce] = useState(0);
  const semanas = usePldSemanas(mesRef, submercado, decompNonce);
  // Projeção só faz sentido no mês corrente (precisa de MTD realizado).
  const isMesCorrente = mesRef === mesRefCorrente();
  const projecao = usePldProjecao(mesRef, submercado, decompNonce);
  // #26: log as-of (track record) — qual era a projeção na época pros dias já
  // fechados. Sobreposto só onde o log TEM dado (não fabrica).
  const projLog = usePldProjecaoLog(submercado);

  function handleDecompSaved() {
    // Fura os dois caches module-level e bumpa o nonce → ambos os hooks
    // refetcham: KPI/projeção E DECOMP/PLD médio efetivo/DIF da tabela (#222).
    invalidatePldProjecaoCache();
    invalidatePldSemanasCache();
    setDecompNonce((n) => n + 1);
  }

  const pontos = useMemo(() => {
    const dados = overview.data?.dados_diarios ?? [];
    return dados.map((d) => ({
      dia: d.data.slice(8, 10), // "01".."31"
      pld: d.pld_media_dia,
    }));
  }, [overview.data]);

  // #26: por data_alvo, a projeção AS-OF = pld_projetado do snapshot mais RECENTE
  // com realizado_no_snapshot=false (último forecast ANTES do dia realizar). Só
  // entradas pré-realização — não usa o snapshot pós-fato (que já é o realizado).
  const asofPorDia = useMemo(() => {
    const best = new Map<string, { asof: string; val: number }>();
    for (const it of projLog.data?.itens ?? []) {
      if (it.realizado_no_snapshot || it.pld_projetado == null) continue;
      const cur = best.get(it.data_alvo);
      if (!cur || it.as_of_date > cur.asof) {
        best.set(it.data_alvo, { asof: it.as_of_date, val: it.pld_projetado });
      }
    }
    const m = new Map<string, number>();
    for (const [d, { val }] of best) m.set(d, val);
    return m;
  }, [projLog.data]);

  // Task #209: pontos mesclados realizado(sólido) + projetado(tracejado) + banda
  // P10–P90, separados pelo flag `realizado` do endpoint. O dia projetado emenda
  // no último realizado (incluímos o valor do boundary na série projetada).
  // #26: série `asof` = track record sobre os dias JÁ REALIZADOS (null no futuro).
  const projChart = useMemo(() => {
    const dias = projecao.data?.dias ?? [];
    if (dias.length === 0) return null;
    const lastRealIdx = dias.reduce((acc, d, i) => (d.realizado ? i : acc), -1);
    const points = dias.map((d, i) => ({
      dia: d.data.slice(8, 10),
      realizado: d.realizado ? d.pld_projetado : null,
      projetado: !d.realizado || i === lastRealIdx ? d.pld_projetado : null,
      banda: !d.realizado && d.p10 != null && d.p90 != null ? [d.p10, d.p90] : null,
      asof: d.realizado ? (asofPorDia.get(d.data) ?? null) : null,
    }));
    const temProjecao = dias.some((d) => !d.realizado && d.pld_projetado != null);
    const temAsof = points.some((p) => p.asof != null);
    return { points, temProjecao, temAsof };
  }, [projecao.data, asofPorDia]);

  if (overview.error) return <PldError />;
  if (overview.isLoading || !overview.data) return <PldLoading />;

  if (overview.data.dados_diarios.length === 0) {
    return <PldEmpty />;
  }

  const ov = overview.data;
  const cor = SUBMERCADO_COLOR[submercado];

  // Projeção exibida só no mês corrente E quando o backend devolveu dias futuros.
  const proj = projecao.data;
  const mostrarProjecao = isMesCorrente && !!projChart?.temProjecao && !!proj;
  const premissa = proj?.premissa_decomp ?? null;
  // #36: versão do modelo LIDA do backend (modelo_versao) — não hardcodar.
  // Fallback pro texto antigo se o campo vier ausente (resposta legada).
  const modeloVersao = proj?.modelo_versao ?? "v1.5";

  return (
    <div data-testid="pld-visao-geral">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label="PLD Realizado (mês)"
          value={fmtPld(ov.pld_realizado_medio)}
          sub={`média ${SUBMERCADO_LABEL[submercado]} até hoje`}
          highlight
          testId="pld-kpi-realizado"
        />
        <KpiCard
          label="Semana Atual"
          value={fmtPld(ov.pld_semana_atual)}
          sub="semana operativa em curso"
          testId="pld-kpi-semana"
        />
        <KpiCard
          label="PLD Mín"
          value={fmtPld(ov.pld_min?.valor ?? null)}
          sub={ov.pld_min ? fmtDateBR(ov.pld_min.data) : undefined}
          subTone="positive"
          testId="pld-kpi-min"
        />
        <KpiCard
          label="PLD Máx"
          value={fmtPld(ov.pld_max?.valor ?? null)}
          sub={ov.pld_max ? fmtDateBR(ov.pld_max.data) : undefined}
          subTone="negative"
          testId="pld-kpi-max"
        />
        {mostrarProjecao && (
          <KpiCard
            label="PLD Projetado (fechamento)"
            value={fmtPld(proj!.fechamento_projetado)}
            sub={`P10 ${fmtPld(proj!.fechamento_p10)} – P90 ${fmtPld(proj!.fechamento_p90)}`}
            highlight
            testId="pld-kpi-projetado"
          />
        )}
      </div>

      {isMesCorrente && (
        <AtualizarDecompForm
          mesRef={mesRef}
          submercado={submercado}
          onSaved={handleDecompSaved}
        />
      )}

      {mostrarProjecao && premissa && (
        <p
          className="text-white/40 text-[11px] mb-4"
          data-testid="pld-projecao-caption"
        >
          Projeção <span className="text-white/60">{proj!.modelo_versao}</span> ·{" "}
          ancorada na <span className="text-white/60">{premissa.revisao}</span> DECOMP
          (vigente desde {fmtDateBR(premissa.vigente_desde)}) · linha cheia = realizado ·
          tracejada = projeção · área = banda P10–P90.
        </p>
      )}

      <div className="mb-6">
        <p className="text-white/50 text-xs mb-2">
          PLD diário {SUBMERCADO_LABEL[submercado]} — R$/MWh{" "}
          {mostrarProjecao ? "(realizado + projeção do fechamento)" : "(realizado do mês)"}
        </p>
        {mostrarProjecao && projChart ? (
          <PldLineChart
            points={projChart.points}
            xKey="dia"
            series={
              [
                { key: "realizado", label: "Realizado", color: cor },
                // #26: track record — projeção as-of sobre os dias já fechados.
                // Só entra se o log tiver dado (guarda contra fabricar série vazia).
                ...(projChart.temAsof
                  ? [{ key: "asof", label: "Projeção as-of", color: COR_ASOF, dashed: true }]
                  : []),
                { key: "projetado", label: `Projeção ${modeloVersao}`, color: COR_PROJECAO, dashed: true },
              ] satisfies PldSeries[]
            }
            band={{ key: "banda", color: COR_PROJECAO, label: "Banda P10–P90" } satisfies PldBand}
            testId="pld-visao-chart"
          />
        ) : (
          <PldLineChart
            points={pontos}
            xKey="dia"
            series={[{ key: "pld", label: SUBMERCADO_LABEL[submercado], color: cor }]}
            testId="pld-visao-chart"
          />
        )}
        {/* #93: teto do log as-of DECLARADO — se o backend truncou (descarta o
            mais velho), a tela diz, em vez de deixar a série "morrer" muda. */}
        {mostrarProjecao && projLog.data?.teto?.truncado && (
          <p
            className="text-white/35 text-[10px] leading-snug mt-2"
            data-testid="pld-projlog-teto"
          >
            Track record as-of parcial: {projLog.data.teto.devolvidas} de{" "}
            {projLog.data.teto.total} snapshots (teto {projLog.data.teto.limite};
            os mais antigos ficaram de fora).
          </p>
        )}
        {/* #36: disclaimer curto da metodologia da projeção (cinza, 2 linhas). */}
        {mostrarProjecao && (
          <p
            className="text-white/35 text-[10px] leading-snug mt-2"
            data-testid="pld-projecao-disclaimer"
          >
            Projeção {modeloVersao}: âncora DECOMP da semana operativa × forma
            intra-semana por regime (úmido/seco) × persistência AR(1) nos primeiros
            dias projetados; feriados nacionais tratados. Dias passados = PLD
            realizado (CCEE); dias futuros = projeção.
          </p>
        )}
      </div>

      <div>
        <h3 className="text-white font-semibold text-sm mb-2">
          Resumo por Semana Operativa ONS
        </h3>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-indra-azul-surface text-white/60 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Revisão</th>
                <th className="px-3 py-2 text-left">Período</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th
                  className="px-3 py-2 text-right"
                  title={`PLD médio da semana: realizado nos dias passados + projeção (modelo ${modeloVersao}) nos dias futuros. Valores em âmbar/itálico incorporam projeção.`}
                >
                  PLD médio
                </th>
                <th className="px-3 py-2 text-right">DECOMP</th>
                <th
                  className="px-3 py-2 text-right"
                  title="DIF = PLD médio (Dessem realizado/projetado) − DECOMP. Negativo = abaixo da REV; positivo = acima."
                >
                  DIF DECOMP
                </th>
                <th
                  className="px-3 py-2 text-right"
                  title="Dias com PLD realizado na semana (até hoje); a semana operativa tem 7 dias (sáb–sex)."
                >
                  Dias realiz.
                </th>
              </tr>
            </thead>
            <tbody>
              {(semanas.data?.semanas ?? []).map((s) => {
                const badge = statusBadge(s.status);
                // #217: a semana incorpora projeção quando ainda não terminou
                // (em curso/pendente) e há valor efetivo — destaca como estimativa.
                const efetivoEstimado =
                  s.status !== "realizada" && s.pld_medio_efetivo != null;
                // #229: a linha cross-month é a REV0 do PRÓXIMO mês (cobre os dias
                // finais do mês corrente). Destacada com acento à esquerda + badge
                // do mês de origem, pra não confundir com uma REV do mês corrente.
                const proxLabel = s.mes_ref_origem ? mesLabel(s.mes_ref_origem) : null;
                return (
                  <tr
                    key={s.cross_month ? `${s.mes_ref_origem}-${s.revisao}` : s.revisao}
                    className={`border-t hover:bg-white/5 ${
                      s.cross_month
                        ? "border-indra-turquesa/30 border-l-2 border-l-indra-turquesa/50 bg-indra-turquesa/[0.04]"
                        : "border-white/5"
                    }`}
                    data-testid={s.cross_month ? "pld-semana-cross-row" : "pld-semana-row"}
                  >
                    <td className="px-3 py-2 text-white/90 font-semibold">
                      <span>{s.revisao}</span>
                      {s.cross_month && proxLabel && (
                        <span
                          className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indra-turquesa/15 text-indra-turquesa align-middle"
                          title={`Próximo mês: REV0 de ${proxLabel} — cobre os dias finais do mês corrente.`}
                          data-testid="pld-semana-cross-badge"
                        >
                          {proxLabel} (próx.)
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-white/70">
                      {fmtDateBR(s.periodo_inicio)} – {fmtDateBR(s.periodo_fim)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] ${badge.className}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-mono ${
                        efetivoEstimado ? "italic" : "text-white/90"
                      }`}
                      style={efetivoEstimado ? { color: COR_PROJECAO } : undefined}
                      title={efetivoEstimado ? `Inclui projeção (${modeloVersao})` : undefined}
                      data-testid="pld-semana-medio"
                    >
                      {fmtPld(s.pld_medio_efetivo)}
                    </td>
                    <td
                      className="px-3 py-2 text-right font-mono text-white/90"
                      data-testid="pld-semana-decomp"
                    >
                      {s.pld_decomp == null ? "—" : fmtPld(s.pld_decomp)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-mono ${
                        s.dif_decomp == null ? "text-white/40" : classBySign(s.dif_decomp)
                      }`}
                      data-testid="pld-semana-dif"
                    >
                      {fmtDif(s.dif_decomp)}
                    </td>
                    <td
                      className="px-3 py-2 text-right text-white/60"
                      // #229: na linha cross-month o denominador é o nº de dias
                      // do mês corrente cobertos pela REV0 do próximo (ex.: 4),
                      // não /7 — a semana só pertence parcialmente a este mês.
                      title={
                        s.cross_month && s.dias_mes_corrente != null
                          ? `${s.dias} dos ${s.dias_mes_corrente} dias deste mês cobertos pela REV0 do próximo mês`
                          : undefined
                      }
                      data-testid="pld-semana-dias"
                    >
                      {s.dias}/
                      {s.cross_month && s.dias_mes_corrente != null
                        ? s.dias_mes_corrente
                        : 7}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
